import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LuChevronRight, LuRefreshCw } from 'react-icons/lu';
import { reportRowLabels } from '../../utils/weekFormat.js';
import { cx } from '../../utils/cx.js';
import { withJail } from './jail.js';
import ReportStatusBadges from './ReportStatusBadges';
import IconButton from '../ui/IconButton';

/** Week number tile: "SEM." over the number, the reading anchor of every row. */
export const WeekTile = ({ week, solid = false, large = false }) => (
  <span
    className={cx(
      'flex flex-shrink-0 flex-col items-center justify-center',
      large ? 'h-14 w-14 rounded-2xl' : 'h-11 w-11 rounded-xl',
      solid ? 'bg-brand-600 text-white shadow-sm' : 'bg-brand-50 text-brand-600 dark:bg-brand-900 dark:text-brand-300',
    )}
    aria-hidden
  >
    <span className="text-[9px] font-bold uppercase leading-none tracking-wider opacity-70">Sem.</span>
    <span className={cx('mt-0.5 font-extrabold leading-none tabular-nums', large ? 'text-xl' : 'text-base')}>{week}</span>
  </span>
);

/**
 * Reports of a client grouped by year, newest first. `weeks` is already sorted; `overlaps`
 * comes from the full list (a filter must not hide the previous report). `onRegenerate` (admin).
 */
export const WeeklyReportList = ({ weeks = [], clientSlug, isAdminUser, jail = null, overlaps = new Map(), onRegenerate = null }) => {
  const years = useMemo(() => {
    const groups = new Map();
    weeks.forEach((week) => {
      const key = String(week.year);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(week);
    });
    return [...groups.entries()];
  }, [weeks]);

  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-900">
      {years.map(([year, items]) => (
        <section key={year} aria-labelledby={`year-${year}`}>
          <h3
            id={`year-${year}`}
            className="flex items-center justify-between bg-gray-50/80 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:bg-night-850 dark:text-gray-500 sm:px-5"
          >
            <span>{year}</span>
            <span className="font-semibold normal-case tracking-normal">{items.length} rapport{items.length > 1 ? 's' : ''}</span>
          </h3>
          <ul className="divide-y divide-gray-100 dark:divide-gray-900">
            {items.map((week) => {
              const overlap = overlaps.get(week.id) || null;
              const { title, subtitle } = reportRowLabels(week, overlap);
              const badges = <ReportStatusBadges report={week} isAdminUser={isAdminUser} overlap={overlap} size="sm" />;
              return (
                <li key={week.id} className="flex items-center">
                  <Link
                    to={withJail(`/w/${clientSlug}/${week.id}`, jail)}
                    className="group flex min-w-0 flex-1 items-center gap-4 px-4 py-3 outline-none transition-colors hover:bg-gray-50 focus-visible:bg-brand-50/60 dark:hover:bg-white/[0.03] dark:focus-visible:bg-brand-950 sm:px-5"
                  >
                    <WeekTile week={week.week_number} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>
                      <span className="mt-1.5 flex flex-wrap gap-1.5 sm:hidden">{badges}</span>
                    </span>
                    <span className="hidden flex-wrap items-center justify-end gap-1.5 sm:flex">{badges}</span>
                    <LuChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-400" aria-hidden />
                  </Link>
                  {onRegenerate && (
                    <IconButton
                      icon={LuRefreshCw}
                      label={`Régénérer la semaine ${week.week_number}`}
                      onClick={() => onRegenerate(week)}
                      disabled={week.steps?.dump === 'started'}
                      className="mr-2 sm:mr-3"
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default WeeklyReportList;
